<template>
  <div class="toolbar-wrapper">
    <div class="toolbar">
      <div class="toolbar-left">
        <h2 class="toolbar-title">Tasks</h2>
        <div class="toolbar-divider"></div>

        <button class="toolbar-button primary" @click="generateExcel">
          Generate Excel
        </button>

        <button class="toolbar-button primary" @click="adjustSize">
          {{ adjustText }}
        </button>

        <button class="toolbar-button primary" @click="addRow">
          Add Row
        </button>

        <div class="toolbar-divider"></div>

        <div class="file-input-wrapper">
          <input
            type="file"
            id="fileInput"
            ref="fileInput"
            accept=".xlsx,.xls"
            @change="handleFileChange"
            style="display: none;"
          />
          <label for="fileInput" class="file-input-label">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path
                d="M16.5,6V17.5A4,4 0 0,1 12.5,21.5A4,4 0 0,1 8.5,17.5V5A2.5,2.5 0 0,1 11,2.5A2.5,2.5 0 0,1 13.5,5V15.5A1,1 0 0,1 12.5,16.5A1,1 0 0,1 11.5,15.5V6H10V15.5A2.5,2.5 0 0,0 12.5,18A2.5,2.5 0 0,0 15,15.5V5A4,4 0 0,0 11,1A4,4 0 0,0 7,5V17.5A5.5,5.5 0 0,0 12.5,23A5.5,5.5 0 0,0 18,17.5V6H16.5Z"
              />
            </svg>
            <span v-if="!file">Load excel file</span>
            <span v-else class="file-name">{{ file.name }}</span>
          </label>
        </div>
      </div>

      <div class="toolbar-right">
        <button class="toolbar-button primary" @click="undoDelete">
          UNDELETE
        </button>

        <button class="toolbar-button primary" @click="toggleWeight">
          {{ showWeight }}
        </button>

        <button class="toolbar-button primary" @click="addColumn('Status')">
          ADD STATUS COLUMN
        </button>

        <button class="toolbar-button primary" @click="addColumn('Responsible')">
          ADD RESPONSIBLE COLUMN
        </button>

        <div class="user-profile" @click="setProfileDialog">
          <img :src="getUserImage()" :alt="userType" class="profile-img" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ExcelOperator from "../../utils/excel-operator";
import { mapState, mapGetters } from "vuex";
import FulfilmentsUtil from "../../utils/fulfilments-util";

const util = new FulfilmentsUtil();

export default {
  name: "Toolbar",
  props: {
    showWeight: {
      type: String,
      default: "Show Weight",
    },
    deliverablesData: {
      type: Array,
      default: null,
    },
    includeFormulas: {
      type: Boolean,
      default: true,
    },
    includeChildren: {
      type: Boolean,
      default: true,
    },
    isRootLevel: {
      type: Boolean,
      default: true,
    },
    stepsFulfillment: {
      type: Object,
      default: null,
    },
    totalFulfillment: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      horizontalAdjusted: false,
      adjustText: "Fit On Page",
      heightScale: 0,
      widthScale: 0,
      file: null,
      stepOffset: 3,
      rules: {
        counter: (value) => value.length <= 20 || "Max 20 characters",
        required: (value) => !!value || "Required.",
        alreadyExist: (value) => {
          return (
            !this.headers.find((header) => value.toLowerCase() === header.text.toLowerCase()) ||
            "Column name already exist"
          );
        },
      },
    };
  },
  computed: {
    ...mapState(["statusTable"]),
    ...mapGetters({
      getAuth: "getAuth",
      getSubID: "getSubID",
      getSubStatus: "getSubStatus",
      stepsFulfillmentGetter: "stepsFulfillment",
      totalFulfillmentGetter: "totalFulfillment",
      getNextColumnName: "getNextColumnName",
      nextResponsibleIdentity: "nextResponsibleIdentity",
    }),

    userType() {
      if (this.isAuthenticated && this.subStatus === "active") {
        return "subscribed";
      } else if (this.isAuthenticated) {
        return "login";
      }
      return "user";
    },

    isAuthenticated() {
      return this.getAuth === "signed-in";
    },

    subStatus() {
      return this.getSubStatus;
    },

    headers() {
      return this.statusTable.headers;
    },

    deliverables() {
      return Array.isArray(this.deliverablesData) ? this.deliverablesData : this.statusTable.deliverables;
    },

    responsibles() {
      return this.statusTable.responsibles;
    },

    steps() {
      return this.statusTable.steps;
    },
    exportStepsFulfillment() {
      return this.stepsFulfillment || this.stepsFulfillmentGetter;
    },
    exportTotalFulfillment() {
      if (this.totalFulfillment !== null && this.totalFulfillment !== undefined) return this.totalFulfillment;
      return this.totalFulfillmentGetter;
    },
  },
  methods: {
    /**
     * Generates and downloads an Excel file with current data
     */
    async generateExcel() {
      const numericTotal = Number(this.exportTotalFulfillment);
      const safeTotal = Number.isFinite(numericTotal) ? numericTotal : 0;
      const excelOperator = new ExcelOperator(
        this.deliverables,
        this.steps,
        this.responsibles,
        this.exportStepsFulfillment,
        Math.round(safeTotal)
      );

      await excelOperator.saveAsExcel({
        deliverables: this.deliverables,
        headers: this.headers,
        steps: this.steps,
        responsibles: this.responsibles,
        stepsFulfillment: this.exportStepsFulfillment,
        totalFulfillment: Math.round(safeTotal),
        includeFormulas: this.includeFormulas,
        includeChildren: this.includeChildren,
      });
    },

    /**
     * Loads data from an uploaded Excel file
     */
    async loadExcel() {
      if (!this.file) return;

      const excelOperator = new ExcelOperator(
        this.deliverables,
        this.steps,
        this.responsibles,
        this.stepsFulfillment,
        Math.round(this.totalFulfillment)
      );

      const loadedData = await excelOperator.loadExcel({ file: this.file });

      // Recalculate fulfillments
      util.recalculateAllFulfillments(loadedData.deliverables, loadedData.steps);

      // Update store
      this.$store.dispatch("setDeliverables", loadedData.deliverables);
      this.$store.dispatch("setSteps", loadedData.steps);
      this.$store.dispatch("setResponsibles", loadedData.responsibles);
      this.$store.dispatch("setHeaders", loadedData.headers);
      this.$store.dispatch("setRowCounterToZero", 0);
    },

    /**
     * Handles file input change event
     */
    handleFileChange(event) {
      this.file = event.target.files[0];
      if (this.file) {
        this.loadExcel();
      }
    },

    /**
     * Adds a new column (Status or Responsible)
     */
    addColumn(columnType) {
      if (!this.isRootLevel) {
        this.$store.dispatch("addToast", {
          severity: "warn",
          life: 3000,
          summary: "Oops!",
          detail: "Column can only be added at the root level.",
        });
        return;
      }

      const nextName = this.getNextColumnName(columnType);

      if (!this.validateColumnName(nextName)) return;

      if (columnType === "Status") {
        this.addStatusColumn(nextName);
      } else {
        this.addResponsibleColumn(nextName);
      }

      this.updateStoreAfterColumnAdd();
    },

    /**
     * Validates column name against rules
     */
    validateColumnName(name) {
      return this.rules.required(name) === true && this.rules.alreadyExist(name) === true;
    },

    /**
     * Adds a new status (step) column
     */
    addStatusColumn(stepText) {
      const stepIdentity = `step${this.steps.length + 1}`;

      // Add to headers
      this.headers.splice(this.headers.length - 1, 0, {
        // Add to headers
        text: stepText,
        value: stepIdentity,
        editable: true,
        sortable: false,
        headerEditable: true,
        columnDeleteIcon: false,
      });

      // Add to steps
      this.steps.push({ text: stepText, identity: stepIdentity });

      // Initialize column values in all leaf deliverables (handles nested subprojects)
      const setStepOnLeaves = (items) => {
        if (!Array.isArray(items)) return;
        items.forEach((item) => {
          if (item && Array.isArray(item.children) && item.children.length > 0) {
            setStepOnLeaves(item.children);
          } else if (item) {
            this.$set(item, stepIdentity, "open");
          }
        });
      };

      setStepOnLeaves(this.deliverables);

      // Recalculate fulfillments
      util.recalculateAllFulfillments(this.deliverables, this.steps);

      this.$store.dispatch("setSteps", this.steps);
    },

    /**
     * Adds a new responsible column
     */
    addResponsibleColumn(responsibleText) {
      const responsibleIdentity = this.nextResponsibleIdentity;

      // Add to headers at correct position
      this.headers.splice(this.responsibles.length + this.stepOffset, 0, {
        text: responsibleText,
        value: responsibleIdentity,
        editable: true,
        sortable: false,
        headerEditable: true,
        columnDeleteIcon: false,
      });

      // Add to responsibles
      this.responsibles.push({
        text: responsibleText,
        identity: responsibleIdentity,
      });

      this.$store.dispatch("setResponsibles", this.responsibles);
    },

    /**
     * Updates store after adding a column
     */
    updateStoreAfterColumnAdd() {
      this.$store.dispatch("setHeaders", this.headers);
      this.$store.dispatch("setDeliverables", this.deliverables);
      this.$store.dispatch("setNextColumnName", "Status");
    },

    /**
     * Adjusts table size to fit on page or return to normal
     */
    adjustSize() {
      const container = this.$el.closest(".data-table-container");
      const table = container?.querySelector("table");
      const wrapper = container?.querySelector(".table-scroll-wrapper");

      if (!table || !wrapper) return;

      if (this.adjustText === "Normal Size") {
        this.resetTableSize(table, wrapper);
      } else {
        this.fitTableToPage(table, wrapper);
      }
    },

    /**
     * Resets table to normal size
     */
    resetTableSize(table, wrapper) {
      if (!this.horizontalAdjusted) {
        wrapper.style.width = wrapper.clientWidth * this.heightScale + "px";
      }
      table.style.transform = "scale(1)";
      this.adjustText = "Fit On Page";
    },

    /**
     * Scales table to fit the page
     */
    fitTableToPage(table, wrapper) {
      const { clientWidth, clientHeight, scrollWidth, scrollHeight } = wrapper;

      this.widthScale = clientWidth / scrollWidth;
      this.heightScale = clientHeight / scrollHeight;

      if (this.widthScale < this.heightScale) {
        table.style.transform = `scale(${this.widthScale})`;
        table.style.transformOrigin = "top left";
        this.horizontalAdjusted = true;
      } else {
        table.style.transform = `scale(${this.heightScale})`;
        table.style.transformOrigin = "top left";
        wrapper.style.width = clientWidth / this.heightScale + "px";
        this.horizontalAdjusted = false;
      }

      this.adjustText = "Normal Size";
    },

    /**
     * Gets the appropriate user profile image
     */
    getUserImage() {
      try {
        return require(`@/assets/user/${this.userType}.png`);
      } catch (e) {
        return 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="%23666" d="M12,4A4,4 0 0,1 16,8A4,4 0 0,1 12,12A4,4 0 0,1 8,8A4,4 0 0,1 12,4M12,14C16.42,14 20,15.79 20,18V20H4V18C4,15.79 7.58,14 12,14Z"/></svg>';
      }
    },

    /**
     * Event emitters
     */
    setProfileDialog() {
      this.$store.dispatch("toggleProfileDialog");
    },

    addRow() {
      this.$emit("add-row");
    },

    toggleWeight() {
      this.$emit("show-weight");
    },

    undoDelete() {
      this.$emit("undelete");
    },
  },
};
</script>
<style scoped>
.toolbar-wrapper {
  width: 100%;
  background-color: white;
  /* border-bottom: 1px solid #e0e0e0; */
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  min-height: 48px;
  background-color: white;
}

/* .elevation-1 {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
} */

.toolbar-left,
.toolbar-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.toolbar-title {
  margin: 0;
  font-size: 20px;
  font-weight: 500;
  color: #333;
}

.toolbar-divider {
  width: 1px;
  height: 32px;
  background-color: #e0e0e0;
  margin: 0 4px;
}

/* Buttons */
.toolbar-button {
  padding: 6px 16px;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.toolbar-button.primary {
  background-color: #1976d2;
  color: white;
}

.toolbar-button.primary:hover {
  background-color: #1565c0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.toolbar-button.primary:active {
  transform: scale(0.98);
}

.toolbar-button.secondary {
  background-color: #f5f5f5;
  color: #666;
}

.toolbar-button.secondary:hover {
  background-color: #e0e0e0;
}

.toolbar-button.secondary:active {
  transform: scale(0.98);
}

/* File Input */
.file-input-wrapper {
  position: relative;
}

.file-input-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background-color: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  color: #666;
  transition: all 0.2s;
}

.file-input-label:hover {
  background-color: #e0e0e0;
  border-color: #ccc;
}

.file-input-label svg {
  flex-shrink: 0;
}

.file-name {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 11px;
  color: #1976d2;
}

/* User Profile */
.user-profile {
  cursor: pointer;
  /* padding: 4px; */
  border-radius: 50%;
  transition: background-color 0.2s;
}

.user-profile:hover {
  background-color: #f5f5f5;
}

.profile-img {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  filter: brightness(0) saturate(100%) invert(12%) sepia(62%) saturate(3136%) hue-rotate(210deg) brightness(96%)
    contrast(97%);
}

/* Responsive */
@media (max-width: 1024px) {
  .toolbar {
    flex-wrap: wrap;
    gap: 8px;
  }

  .toolbar-left,
  .toolbar-right {
    gap: 8px;
  }

  .toolbar-button {
    padding: 4px 12px;
    font-size: 11px;
  }

  .toolbar-title {
    font-size: 18px;
  }
}

@media (max-width: 768px) {
  .toolbar {
    padding: 8px;
  }

  .toolbar-divider {
    display: none;
  }

  .file-name {
    max-width: 100px;
  }
}
</style>
